/**
 * What a block is made of.
 *
 * One classification, used for everything that depends on the substance
 * rather than on the particular block: which tool cuts it, and what it sounds
 * like when it comes apart. Those two started as separate tables - the sound
 * map came first - and separate tables that mean the same thing drift: a block
 * added to one and forgotten in the other sounds like stone and digs like
 * earth, and nothing complains.
 *
 * A lookup rather than a field on BlockDef, because the stateful ranges hold
 * thirty-odd ids that all mean "wood", and because two blocks that look
 * nothing alike (цегла and камінь) are the same thing to strike.
 */

import {
  BlockId, isDoor, isShutter, isBed, isRushnyk, isCrop, isWater, isStove,
  isChest, isFlag, isOil,
} from "./registry.js";

export type Material =
  /** Anything you need a pick for: rock, fired clay, brick. */
  | "stone"
  /** Trunks, planks, and everything made of them. */
  | "wood"
  /** Loose ground: soil, sand, raw clay. */
  | "earth"
  /** Growing things and what is cut from them: leaves, crops, straw. */
  | "plant"
  /** Woven or spun: linen, a рушник on the wall. */
  | "cloth"
  | "glass"
  | "fluid"
  /** Air, and anything with no substance to it. */
  | "none";

export function blockMaterial(id: number): Material {
  if (id === BlockId.Air) return "none";
  if (isWater(id) || isOil(id)) return "fluid";

  // The packed ranges first: between them they cover more ids than the enum.
  if (isDoor(id) || isShutter(id) || isBed(id)) return "wood";
  /*
   * A скриня is a box of planks, and until durability arrived nobody could
   * tell: it fell through to the default below, which is earth. It sounded
   * like a shovelful of soil coming apart and, the moment blows started
   * counting, it took four of them instead of the eight a chest was asked
   * for. The default is still earth and there is still a tail of blocks
   * sitting in it - but the ones that are plainly made of something are
   * named here now.
   */
  if (isChest(id)) return "wood";
  // A прапор is cloth on a pole. What it sounds like is cloth; how long it
  // takes is its own number - see BLOWS in breaking.ts.
  if (isFlag(id)) return "cloth";
  if (isRushnyk(id)) return "cloth";
  if (isStove(id)) return "stone";
  if (isCrop(id)) return "plant";

  switch (id) {
    case BlockId.OakLog:
    case BlockId.AcaciaLog:
    case BlockId.PineLog:
    case BlockId.OakPlanks:
    case BlockId.AcaciaPlanks:
    case BlockId.PinePlanks:
    case BlockId.SpinningWheel:
    // A смолоскип is a stick with tar on the end. What it is made of and how
    // long it takes to take down are different questions: see ONE_BLOW.
    case BlockId.Torch:
      return "wood";

    case BlockId.Glass:
    case BlockId.OakWindow:
    case BlockId.AcaciaWindow:
      return "glass";

    // Both of these want a pick, and адамантій wants an iron one - which is
    // `requiredStrength`'s business, not this table's.
    case BlockId.Bedrock:
    case BlockId.Adamant:
    case BlockId.Stone:
    case BlockId.CoalOre:
    case BlockId.IronOre:
    case BlockId.ManganeseOre:
    case BlockId.GraphiteOre:
    case BlockId.OilShale:
    case BlockId.Granite:
    case BlockId.Cobble:
    case BlockId.StoneTile:
    case BlockId.Sandstone:
    case BlockId.Brick:
    // A чан is fired clay - a pot, not a lump of it.
    case BlockId.Vat:
    case BlockId.VatFull:
      return "stone";

    case BlockId.Grass:
    case BlockId.Dirt:
    case BlockId.Chornozem:
    case BlockId.Sand:
    case BlockId.Clay:
    // Мазанка is clay and straw smeared on a wattle frame - earth, whatever it
    // is holding up. Filed with the brick it sits next to, it wanted a pickaxe,
    // and a daub wall you cannot pull apart by hand is simply wrong.
    case BlockId.Mazanka:
    case BlockId.Peat:
    case BlockId.Ilmenite:
    case BlockId.Rutile:
    case BlockId.Farmland:
    case BlockId.FarmlandWet:
    /*
     * Шопопало-блок is earth, which is to say: bare hands will do.
     *
     * It has to be. The map is one block in the void and nothing else - a
     * block that asked for a pick would be a map nobody can start.
     */
    case BlockId.Random:
      return "earth";

    case BlockId.OakLeaves:
    case BlockId.AcaciaLeaves:
    case BlockId.PineLeaves:
    case BlockId.OakSapling:
    case BlockId.AcaciaSapling:
    case BlockId.PineSapling:
    case BlockId.WildWheat:
    case BlockId.WildFlax:
    case BlockId.WildHemp:
    case BlockId.Thatch:
      return "plant";

    default:
      return "earth";
  }
}
